/**
 * Admin Product API Client
 * API methods for admin product operations
 */

import { apiClient } from '../client';

export interface ProductListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: 'active' | 'inactive' | 'hidden';
  productType?: 'hosting' | 'domain' | 'addon' | 'other';
  groupId?: number;
  sortBy?: 'id' | 'name' | 'order' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
}

export interface CreateProductData {
  name: string;
  description?: string;
  productGroupId?: number;
  productType: 'hosting' | 'domain' | 'addon' | 'other';
  welcomeEmail?: number;
  status?: 'active' | 'inactive' | 'hidden';
  stockControl?: boolean;
  stockQuantity?: number;
  order?: number;
  requiresDomain?: boolean;
  autoSetup?: 'on_order' | 'on_payment' | 'manual';
  moduleId?: number;
  paymentType: 'free' | 'onetime' | 'recurring';
  diskSpace?: number;
  bandwidth?: number;
  emailAccounts?: number;
  databases?: number;
  subdomains?: number;
  seoTitle?: string;
  seoDescription?: string;
  tags?: string[];
}

export interface UpdateProductData {
  name?: string;
  description?: string;
  productGroupId?: number | null;
  welcomeEmail?: number | null;
  status?: 'active' | 'inactive' | 'hidden';
  stockControl?: boolean;
  stockQuantity?: number;
  order?: number;
  requiresDomain?: boolean;
  autoSetup?: 'on_order' | 'on_payment' | 'manual';
  moduleId?: number | null;
  paymentType?: 'free' | 'onetime' | 'recurring';
  diskSpace?: number;
  bandwidth?: number;
  emailAccounts?: number;
  databases?: number;
  subdomains?: number;
  seoTitle?: string;
  seoDescription?: string;
  tags?: string[];
}

export interface ProductPricingData {
  currencyId: number;
  billingCycle: string;
  price: number;
  setupFee?: number;
  isEnabled?: boolean;
}

export interface BulkPricingData {
  currencyId: number;
  pricing: Array<{
    billingCycle: string;
    price: number;
    setupFee?: number;
  }>;
}

export interface ProductGroupData {
  name: string;
  description?: string;
  slug: string;
  order?: number;
  isVisible?: boolean;
  parentId?: number | null;
}

export const productsAdminApi = {
  /**
   * List all products
   */
  list: async (params?: ProductListParams) => {
    const response = await apiClient.get('/admin/products', { params });
    return response.data;
  },

  /**
   * Get single product by ID
   */
  get: async (productId: number) => {
    const response = await apiClient.get(`/admin/products/${productId}`);
    return response.data;
  },

  /**
   * Create new product
   */
  create: async (data: CreateProductData) => {
    const response = await apiClient.post('/admin/products', data);
    return response.data;
  },

  /**
   * Update product
   */
  update: async (productId: number, data: UpdateProductData) => {
    const response = await apiClient.put(`/admin/products/${productId}`, data);
    return response.data;
  },

  /**
   * Delete product
   */
  delete: async (productId: number) => {
    const response = await apiClient.delete(`/admin/products/${productId}`);
    return response.data;
  },

  /**
   * Get product pricing
   */
  getPricing: async (productId: number) => {
    const response = await apiClient.get(`/admin/products/${productId}/pricing`);
    return response.data;
  },

  /**
   * Set product pricing (single)
   */
  setPricing: async (productId: number, data: ProductPricingData) => {
    const response = await apiClient.post(`/admin/products/${productId}/pricing`, data);
    return response.data;
  },

  /**
   * Set bulk pricing (multiple cycles)
   */
  setBulkPricing: async (productId: number, data: BulkPricingData) => {
    const response = await apiClient.post(`/admin/products/${productId}/pricing/bulk`, data);
    return response.data;
  },

  /**
   * Delete pricing
   */
  deletePricing: async (productId: number, pricingId: number) => {
    const response = await apiClient.delete(`/admin/products/${productId}/pricing/${pricingId}`);
    return response.data;
  },

  // Product Groups
  listGroups: async () => {
    const response = await apiClient.get('/admin/products/groups/list');
    return response.data;
  },

  createGroup: async (data: ProductGroupData) => {
    const response = await apiClient.post('/admin/products/groups/create', data);
    return response.data;
  },

  updateGroup: async (groupId: number, data: Partial<ProductGroupData>) => {
    const response = await apiClient.put(`/admin/products/groups/${groupId}`, data);
    return response.data;
  },

  deleteGroup: async (groupId: number) => {
    const response = await apiClient.delete(`/admin/products/groups/${groupId}`);
    return response.data;
  },
};
